import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Typography, Collapse } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 16px;
  background: ${(props) => getThemeValue(props, '#171923', 'transparent')};
  color: ${(props) => getThemeValue(props, '#fff', 'inherit')};
`;

export const HeaderSection = styled.div`
  margin-bottom: 24px;
`;

export const DashboardTitle = styled(Typography.Title)`
  margin: 0 !important;
`;

export const SectionCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#718096', '#DCDCDC')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};

  .ant-card-body {
    padding: 20px;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const SectionTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')} !important;
  font-size: 18px !important;
  font-weight: 700 !important;
`;

export const CloseButton = styled(Button)`
  border-radius: 8px !important;
`;

export const StyledCollapse = styled(Collapse)`
  .ant-collapse-panel > .ant-collapse-body {
    padding: 0 !important;
  }
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ViewAllButton = styled(Button)`
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #ffffff !important;
  border-radius: 8px !important;
  font-weight: 500 !important;

  &:hover:not(:disabled) {
    background-color: #1e4d8f !important;
    border-color: #1e4d8f !important;
    color: #ffffff !important;
  }

  &:disabled {
    background-color: #2D6CCA !important;
    border-color: #2D6CCA !important;
    color: #ffffff !important;
    opacity: 1 !important;
    cursor: not-allowed !important;
  }
`;

export const SkeletonCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props: any) => (props.theme?.mode === 'dark' ? '#718096' : '#DCDCDC')};
  background: ${(props: any) => (props.theme?.mode === 'dark' ? '#1a202c' : '#ffffff')};

  .ant-card-body {
    padding: 20px;
  }
`;

// Quick Actions Section
export const QuickActionsCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#718096', '#DCDCDC')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  margin-bottom: 24px;

  .ant-card-body {
    padding: 20px;
  }
`;

export const QuickActionsTitle = styled(Typography.Title)`
  margin-bottom: 16px !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')} !important;
  font-size: 18px !important;
  font-weight: 700 !important;
`;

export const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const QuickActionButton = styled(Button)<{ $accentColor?: string }>`
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  border: 2px solid ${(props) => props.$accentColor || '#2D6CCA'} !important;
  background: transparent !important;
  color: ${(props) => props.$accentColor || '#2D6CCA'} !important;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;

  svg {
    font-size: 24px;
  }

  &:hover {
    background: ${(props) => props.$accentColor || '#2D6CCA'}20 !important;
    color: ${(props) => props.$accentColor || '#2D6CCA'} !important;
    border-color: ${(props) => props.$accentColor || '#2D6CCA'} !important;
    box-shadow: 0 4px 12px ${(props) => props.$accentColor || '#2D6CCA'}40;
  }
`;

// Alerts Section
export const AlertsCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#718096', '#DCDCDC')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  margin-bottom: 24px;

  .ant-card-body {
    padding: 20px;
  }
`;

export const AlertsTitle = styled(Typography.Title)`
  margin-bottom: 16px !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')} !important;
  font-size: 18px !important;
  font-weight: 700 !important;
`;

export const AlertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
`;

export const AlertCard = styled.div<{ $severity?: 'warning' | 'error' | 'info' }>`
  border-left: 4px solid ${(props) => {
    switch (props.$severity) {
      case 'error':
        return '#F56565';
      case 'warning':
        return '#ED8936';
      default:
        return '#4299E1';
    }
  }};
  background: ${(props) => {
    const darkBg = props.$severity === 'error' ? '#2d2424' : props.$severity === 'warning' ? '#322b20' : '#1f2640';
    const lightBg = props.$severity === 'error' ? '#FEF2F2' : props.$severity === 'warning' ? '#FFFAF0' : '#EBF8FF';
    return getThemeValue(props, darkBg, lightBg);
  }};
  padding: 16px;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  svg {
    font-size: 24px;
    margin-right: 12px;
  }
`;

export const AlertContent = styled.div`
  flex: 1;
`;

export const AlertTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
`;

export const AlertMessage = styled.div`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#cbd5e0', '#718096')};
`;

// Table Styles
export const TableContainer = styled.div`
  margin-top: 16px;

  .ant-table {
    background: transparent !important;
  }

  .ant-table-thead > tr > th {
    background: transparent !important;
    color: ${(props) => getThemeValue(props, '#cbd5e0', '#4a5568')} !important;
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')} !important;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')} !important;
    color: ${(props) => getThemeValue(props, '#e2e8f0', '#1a202c')};
  }

  .ant-table-tbody > tr:hover > td {
    background: ${(props) => getThemeValue(props, '#2d3748', '#f7fafc')} !important;
  }
`;
